import { Fragment } from 'react';
import { getMeta } from 'constants/meta';
import Head from 'components/Head';
import { utils } from '@sp/cookie-notice-manager';

const PrivacyPolicy = ({ location }) => {
  const { title, description } = getMeta(location.pathname);
  return (
    <Fragment>
      <Head title={title} description={description} />
      <section className="section section_privacy">
        <div className="wrapper">
          <div className="row">
            <div className="col col-10 offset-1 col-tablet-12 offset-tablet-0">
              <div className="section__header-wrap _mb-66 _mb-phone-36">
                <h2 className="title title_size_m _mb-18 section__main-title _mb-phone-10">
                  Privacy Policy
                </h2>
                <h5 className="text section__sub-text">
                  Last modified: 06 December 2018
                </h5>
              </div>
              <div>
                <h5 className="title section__title _mb-17">1. Introduction</h5>
                <p className="text section__text _mb-13">
                  We are committed to safeguarding the privacy of our website
                  visitors, subscribers and resellers.
                </p>
                <p className="text section__text _mb-13">
                  This policy applies where we are acting as a controller with
                  respect to the personal data of our website visitors,
                  subscribers and resellers; in other words, where we determine
                  the purposes and means of the processing of that personal
                  data. In some situations we will act as a processor, not a
                  controller - see Section 13 for details.
                </p>
                <p className="text section__text _mb-13">
                  We use cookies on our website. Insofar as required by
                  applicable law, we will ask you to consent to our use of
                  cookies when you first visit our website. For more information
                  about managing cookies, see Section 15.
                </p>
                <p className="text section__text _mb-23">
                  In this policy, "we", "us" and "our" refer to Siteplus Pte
                  Ltd. For more information about us, see Section 16.
                </p>

                <h5 className="title section__title _mb-17">
                  2. How we use your personal data
                </h5>
                <p className="text section__text _mb-13">
                  In this Section 2 we have set out:
                </p>
                <ul className="list list_disc _mb-29">
                  <li className="text list__item section__text _mb-7">
                    the general categories of personal data that we may process;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    in the case of personal data that we did not obtain directly
                    from you, the source and specific categories of that data;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    the purposes for which we may process personal data; and
                  </li>
                  <li className="text list__item section__text">
                    the legal bases of the processing.
                  </li>
                </ul>

                <table className="table section__table section__table_mobile _mb-30">
                  <tbody className="table__body">
                    <tr className="table__row">
                      <th className="table__header">Category</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">Usage data</td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Details</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Data about your use of our website and services,
                        including your IP address, geographical location,
                        browser type and version, operating system, referral
                        source, length of visit, page views and website
                        navigation paths, as well as information about the
                        timing, frequency and pattern of your service use
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Source</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Our website analytics systems
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Purposes</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Monitoring and analysing the use of our website and
                        services
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Legal basis</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Legitimate interests, namely improving our website and
                        services
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table className="table section__table section__table_mobile _mb-30">
                  <tbody className="table__body">
                    <tr className="table__row">
                      <th className="table__header">Category</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">Account data</td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Details</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Your name, email address, telephone number, postal
                        address, business name, business website URL and account
                        preferences
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Source</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        <div className="_mb-15">You or your employer</div>
                        We may also obtain this data from your Google Account,
                        via the Google API
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Purposes</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Operating our website, providing our services, ensuring
                        the security of our website and services, maintaining
                        back-ups of our databases and communicating with you
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Legal basis</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Legitimate interests, namely the proper administration
                        of our website and business; and/or the performance of a
                        contract between you and us and/or taking steps, at your
                        request, to enter into such a contract
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table className="table section__table section__table_mobile _mb-30">
                  <tbody className="table__body">
                    <tr className="table__row">
                      <th className="table__header">Category</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">Transaction data</td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Details</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Information relating to transactions, including
                        purchases of services, that you enter into with us
                        and/or through our website. The transaction data may
                        include your contact details, elements of your card
                        details and the transaction details
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Source</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        You and/or our payments services providers
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Purposes</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Supplying the purchased services and keeping proper
                        records of transactions
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Legal basis</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Legitimate interests, namely the proper administration
                        of our website and business; and/or the performance of a
                        contract between you and us and/or taking steps, at your
                        request, to enter into such a contract
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table className="table section__table section__table_mobile _mb-30">
                  <tbody className="table__body">
                    <tr className="table__row">
                      <th className="table__header">Category</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Customer relationship data
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Details</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Information relating to our customer relationships,
                        including your name, your employer, your job title or
                        role, your contact details, and information contained in
                        communications between us and you or your employer
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Source</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        <div className="_mb-15">You or your employer</div>
                        We may also obtain this data from your Google Account,
                        via the Google API
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Purposes</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Managing our relationships with customers, communicating
                        with customers, keeping records of those communications
                        and promoting our products and services to customers
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Legal basis</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Our legitimate interests, namely the proper management
                        of our customer relationships
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table className="table section__table section__table_mobile _mb-30">
                  <tbody className="table__body">
                    <tr className="table__row">
                      <th className="table__header">Category</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">Notification data</td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Details</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Information that you provide to us for the purpose of
                        subscribing to our email notifications
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Source</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">You</td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Purposes</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Sending you notifications and monitoring notifications
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Legal basis</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        <div className="_mb-15">Consent</div>
                        You can manage notifications using your account manager
                        on the website
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table className="table section__table section__table_mobile _mb-30">
                  <tbody className="table__body">
                    <tr className="table__row">
                      <th className="table__header">Category</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">Newsletter data</td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Details</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Information that you provide to us for the purpose of
                        subscribing to our newsletters and marketing
                        communications
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Source</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">You</td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Purposes</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Sending you the newsletters and monitoring newsletter
                        communications
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Legal basis</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        <div className="_mb-15">Consent</div>
                        You can manage newsletters using your account manager on
                        the website
                      </td>
                    </tr>
                  </tbody>
                </table>
                <table className="table section__table section__table_mobile _mb-30">
                  <tbody className="table__body">
                    <tr className="table__row">
                      <th className="table__header">Category</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">Communications data</td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Details</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Information contained in or relating to any
                        communication that you send to us (excluding any data
                        which constitutes CRM data), including include the
                        communication content and metadata associated with the
                        communication
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Source</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        You. In addition, our website will generate the metadata
                        associated with communications made using the website
                        contact forms
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Purposes</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Communicating with you and record-keeping
                      </td>
                    </tr>
                    <tr className="table__row">
                      <th className="table__header">Legal basis</th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Legitimate interests, namely the proper administration
                        of our website and business
                      </td>
                    </tr>
                  </tbody>
                </table>

                <table className="table section__table section__table_desktop _mb-30">
                  <tbody className="table__body">
                    <tr className="table__row">
                      <th className="table__header section__table-header section__table-header_first">
                        Category
                      </th>
                      <th className="table__header section__table-header section__table-header_second">
                        Details
                      </th>
                      <th className="table__header section__table-header section__table-header_third">
                        Source
                      </th>
                      <th className="table__header section__table-header">
                        Purposes
                      </th>
                      <th className="table__header section__table-header">
                        Legal basis
                      </th>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">Usage data</td>
                      <td className="table__data">
                        Data about your use of our website and services,
                        including your IP address, geographical location,
                        browser type and version, operating system, referral
                        source, length of visit, page views and website
                        navigation paths, as well as information about the
                        timing, frequency and pattern of your service use
                      </td>
                      <td className="table__data">
                        Our website analytics systems
                      </td>
                      <td className="table__data">
                        Monitoring and analysing the use of our website and
                        services
                      </td>
                      <td className="table__data">
                        Legitimate interests, namely improving our website and
                        services
                      </td>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">Account data</td>
                      <td className="table__data">
                        Your name, email address, telephone number, postal
                        address, business name, business website URL and account
                        preferences
                      </td>
                      <td className="table__data">
                        <div className="_mb-15">You or your employer</div>
                        We may also obtain this data from your Google Account,
                        via the Google API
                      </td>
                      <td className="table__data">
                        Operating our website, providing our services, ensuring
                        the security of our website and services, maintaining
                        back-ups of our databases and communicating with you
                      </td>
                      <td className="table__data">
                        Legitimate interests, namely the proper administration
                        of our website and business; and/or the performance of a
                        contract between you and us and/or taking steps, at your
                        request, to enter into such a contract
                      </td>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">Transaction data</td>
                      <td className="table__data">
                        Information relating to transactions, including
                        purchases of services, that you enter into with us
                        and/or through our website. The transaction data may
                        include your contact details, elements of your card
                        details and the transaction details
                      </td>
                      <td className="table__data">
                        You and/or our payments services providers
                      </td>
                      <td className="table__data">
                        Supplying the purchased services and keeping proper
                        records of transactions
                      </td>
                      <td className="table__data">
                        Legitimate interests, namely the proper administration
                        of our website and business; and/or the performance of a
                        contract between you and us and/or taking steps, at your
                        request, to enter into such a contract
                      </td>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">
                        Customer relationship data
                      </td>
                      <td className="table__data">
                        Information relating to our customer relationships,
                        including your name, your employer, your job title or
                        role, your contact details, and information contained in
                        communications between us and you or your employer
                      </td>
                      <td className="table__data">
                        <div className="_mb-15">You or your employer</div>
                        We may also obtain this data from your Google Account,
                        via the Google API
                      </td>
                      <td className="table__data">
                        Managing our relationships with customers, communicating
                        with customers, keeping records of those communications
                        and promoting our products and services to customers
                      </td>
                      <td className="table__data">
                        Our legitimate interests, namely the proper management
                        of our customer relationships
                      </td>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">Notification data</td>
                      <td className="table__data">
                        Information that you provide to us for the purpose of
                        subscribing to our email notifications
                      </td>
                      <td className="table__data">You</td>
                      <td className="table__data">
                        Sending you notifications and monitoring notifications
                      </td>
                      <td className="table__data">
                        <div className="_mb-15">Consent</div>
                        You can manage notifications using your account manager
                        on the website
                      </td>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">Newsletter data</td>
                      <td className="table__data">
                        Information that you provide to us for the purpose of
                        subscribing to our newsletters and marketing
                        communications
                      </td>
                      <td className="table__data">You</td>
                      <td className="table__data">
                        Sending you the newsletters and monitoring newsletter
                        communications
                      </td>
                      <td className="table__data">
                        <div className="_mb-15">Consent</div>
                        You can manage newsletters using your account manager on
                        the website
                      </td>
                    </tr>
                    <tr className="table__row">
                      <td className="table__data">Communications data</td>
                      <td className="table__data">
                        Information contained in or relating to any
                        communication that you send to us (excluding any data
                        which constitutes CRM data), including include the
                        communication content and metadata associated with the
                        communication
                      </td>
                      <td className="table__data">
                        You. In addition, our website will generate the metadata
                        associated with communications made using the website
                        contact forms
                      </td>
                      <td className="table__data">
                        Communicating with you and record-keeping
                      </td>
                      <td className="table__data">
                        Legitimate interests, namely the proper administration
                        of our website and business
                      </td>
                    </tr>
                  </tbody>
                </table>
                <p className="text section__text _mb-13">
                  We may process any of your personal data identified in this
                  policy where necessary for the establishment, exercise or
                  defence of legal claims, whether in court proceedings or in an
                  administrative or out-of-court procedure. The legal basis for
                  this processing is our legitimate interests, namely the
                  protection and assertion of our legal rights, your legal
                  rights and the legal rights of others.
                </p>
                <p className="text section__text _mb-13">
                  We may process any of your personal data identified in this
                  policy where necessary for the purposes of obtaining or
                  maintaining insurance coverage, managing risks, or obtaining
                  professional advice. The legal basis for this processing is
                  our legitimate interests, namely the proper protection of our
                  business against risks.
                </p>
                <p className="text section__text _mb-23">
                  In addition to the specific purposes for which we may process
                  your personal data set out in this Section 2, we may also
                  process any of your personal data where such processing is
                  necessary for compliance with a legal obligation to which we
                  are subject, or in order to protect your vital interests or
                  the vital interests of another natural person.
                </p>

                <h5 className="title section__title _mb-17">
                  3. Automated decision-making
                </h5>
                <p className="text section__text _mb-13">
                  We will use your personal data for the purposes of automated
                  decision-making in relation to your experience of our website
                  and services.
                </p>
                <p className="text section__text _mb-23">
                  This automated decision-making will involve analysing usage
                  data from you and other users so that we can give you a better
                  experience when building websites using our software.
                </p>

                <h5 className="title section__title _mb-17">
                  4. Providing your personal data to others
                </h5>
                <p className="text section__text _mb-13">
                  We may disclose your personal data to any member of our group
                  of companies (this means our subsidiaries, our ultimate
                  holding company and all its subsidiaries) insofar as
                  reasonably necessary for the purposes, and on the legal bases,
                  set out in this policy.
                </p>
                <p className="text section__text _mb-13">
                  We may disclose your personal data to our insurers and/or
                  professional advisers insofar as reasonably necessary for the
                  purposes of obtaining or maintaining insurance coverage,
                  managing risks, obtaining professional advice, or the
                  establishment, exercise or defence of legal claims, whether in
                  court proceedings or in an administrative or out-of-court
                  procedure.
                </p>
                <p className="text section__text _mb-13">
                  Financial transactions relating to our website and services
                  are handled by our payment services providers, Stripe, Inc and
                  PayPal, Inc. We will share transaction data with our payment
                  services providers only to the extent necessary for the
                  purposes of processing your payments, refunding such payments
                  and dealing with complaints and queries relating to such
                  payments and refunds. You can find information about the
                  payment services providers' privacy policies and practices at{' '}
                  <a
                    className="link link_blue section__link"
                    href="https://stripe.com/gb/privacy"
                    target="_blank"
                  >
                    https://stripe.com/gb/privacy
                  </a>{' '}
                  and{' '}
                  <a
                    className="link link_blue section__link"
                    href="https://www.paypal.com/en/webapps/mpp/ua/privacy-full"
                    target="_blank"
                  >
                    https://www.paypal.com/en/webapps/mpp/ua/privacy-full
                  </a>
                  .
                </p>
                <p className="text section__text _mb-13">
                  We may disclose your personal data, directly or indirectly, to
                  our providers of: (a) hosting, back-up, security and content
                  delivery services; (b) communications services; (c) support
                  services infrastructure – in each case for the purposes
                  described in this policy.
                </p>
                <p className="text section__text _mb-23">
                  In addition to the specific disclosures of personal data set
                  out in this Section 4, we may disclose your personal data
                  where such disclosure is necessary for compliance with a legal
                  obligation to which we are subject, or in order to protect
                  your vital interests or the vital interests of another natural
                  person. We may also disclose your personal data where such
                  disclosure is necessary for the establishment, exercise or
                  defence of legal claims, whether in court proceedings or in an
                  administrative or out-of-court procedure.
                </p>

                <h5 className="title section__title _mb-17">
                  5. International transfers of your personal data
                </h5>
                <p className="text section__text _mb-13">
                  In this Section 5, we provide information about the
                  circumstances in which your personal data may be transferred
                  to countries outside the European Economic Area (EEA).
                </p>
                <p className="text section__text _mb-13">
                  We may make transfers of your personal data to our related
                  companies and services providers in Australia, the
                  Philippines, Singapore, Ukraine, the United Arab Emirates
                  and/or the United States of America for the purpose of
                  enabling the provision of hosting, back-up and content
                  delivery services. These transfers will be protected by
                  appropriate safeguards, such as the use of the standard
                  contractual clauses (
                  <a
                    className="link link_blue section__link"
                    href="https://ec.europa.eu/info/law/law-topic/data-protection/data-transfers-outside-eu/model-contracts-transfer-personal-data-third-countries_en"
                    target="_blank"
                  >
                    https://ec.europa.eu/info/law/law-topic/data-protection/data-transfers-outside-eu/model-contracts-transfer-personal-data-third-countries_en
                  </a>
                  ) and/or the Privacy Shield scheme (
                  <a
                    className="link link_blue section__link"
                    href="https://www.privacyshield.gov"
                    target="_blank"
                  >
                    https://www.privacyshield.gov
                  </a>
                  ).
                </p>
                <p className="text section__text _mb-23">
                  You acknowledge that personal data that you submit for
                  publication through our website or services may be available,
                  via the internet, around the world. We cannot prevent the use
                  (or misuse) of such personal data by others.
                </p>

                <h5 className="title section__title _mb-17">
                  6. Retaining and deleting personal data
                </h5>
                <p className="text section__text _mb-13">
                  This Section 6 sets out our data retention policies and
                  procedure, which are designed to help ensure that we comply
                  with our legal obligations in relation to the retention and
                  deletion of personal data.
                </p>
                <p className="text section__text _mb-13">
                  Personal data that we process for any purpose or purposes
                  shall not be kept for longer than is necessary for that
                  purpose or those purposes.
                </p>
                <p className="text section__text _mb-13">
                  We will retain your personal data as follows: (a) usage data
                  will be retained for a period of up to 7 years following
                  collection; (b) account data will be retained for a period of
                  up to 7 years following the date of closure of your account;
                  (c) customer relationship data, notification data and
                  communication data will be retained for a period of up to 7
                  years following the later of the date of termination of the
                  last contract between you (or your company) and us, and the
                  date of the last communication period between you (or your
                  company) and us; (d) transaction data will be retained for a
                  period of up to 7 years following the date of the transaction;
                  and (e) newsletter data will be retained for a period of up to
                  12 months following the date that you unsubscribe from our
                  newsletter, unless you consent to us using your data to
                  positively suppress further communications.
                </p>
                <p className="text section__text _mb-23">
                  Notwithstanding the other provisions of this Section 6, we may
                  retain your personal data where such retention is necessary
                  for compliance with a legal obligation to which we are
                  subject, or in order to protect your vital interests or the
                  vital interests of another natural person.
                </p>

                <h5 className="title section__title _mb-17">7. Amendments</h5>
                <p className="text section__text _mb-13">
                  We may update this policy from time to time by publishing a
                  new version on our website.
                </p>
                <p className="text section__text _mb-13">
                  You should check this page occasionally to ensure you are
                  happy with any changes to this policy.
                </p>
                <p className="text section__text _mb-23">
                  We may notify you of significant changes to this policy by
                  email or through the private messaging system on our website.
                </p>

                <h5 className="title section__title _mb-17">8. Your rights</h5>
                <p className="text section__text _mb-13">
                  In this Section 8, we have summarised the rights that you have
                  under data protection law. Some of the rights are complex, and
                  not all of the details have been included in our summaries.
                  Accordingly, you should read the relevant laws and guidance
                  from the regulatory authorities for a full explanation of
                  these rights.
                </p>
                <p className="text section__text _mb-13">
                  Your principal rights under data protection law are:
                </p>
                <ul className="list list_disc _mb-13">
                  <li className="text list__item section__text _mb-7">
                    the right to access;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    the right to rectification;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    the right to erasure;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    the right to restrict processing;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    the right to object to processing;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    the right to data portability;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    the right to complain to a supervisory authority; and
                  </li>
                  <li className="text list__item section__text">
                    the right to withdraw consent.
                  </li>
                </ul>
                <p className="text section__text _mb-13">
                  You have the right to confirmation as to whether or not we
                  process your personal data and, where we do, access to the
                  personal data, together with certain additional information.
                  That additional information includes details of the purposes
                  of the processing, the categories of personal data concerned
                  and the recipients of the personal data. Providing the rights
                  and freedoms of others are not affected, we will supply to you
                  a copy of your personal data. The first copy will be provided
                  free of charge, but additional copies may be subject to a
                  reasonable fee.
                </p>
                <p className="text section__text _mb-13">
                  You have the right to have any inaccurate personal data about
                  you rectified and, taking into account the purposes of the
                  processing, to have any incomplete personal data about you
                  completed.
                </p>
                <p className="text section__text _mb-13">
                  In some circumstances you have the right to the erasure of
                  your personal data without undue delay. Those circumstances
                  include: the personal data are no longer necessary in relation
                  to the purposes for which they were collected or otherwise
                  processed; you withdraw consent to consent-based processing;
                  you object to the processing under certain rules of applicable
                  data protection law; the processing is for direct marketing
                  purposes; and the personal data have been unlawfully
                  processed. However, there are exclusions of the right to
                  erasure. The general exclusions include where processing is
                  necessary: for exercising the right of freedom of expression
                  and information; for compliance with a legal obligation; or
                  for the establishment, exercise or defence of legal claims.
                </p>
                <p className="text section__text _mb-13">
                  In some circumstances you have the right to restrict the
                  processing of your personal data. Those circumstances are: you
                  contest the accuracy of the personal data; processing is
                  unlawful but you oppose erasure; we no longer need the
                  personal data for the purposes of our processing, but you
                  require personal data for the establishment, exercise or
                  defence of legal claims; and you have objected to processing,
                  pending the verification of that objection. Where processing
                  has been restricted on this basis, we may continue to store
                  your personal data. However, we will only otherwise process
                  it: with your consent; for the establishment, exercise or
                  defence of legal claims; for the protection of the rights of
                  another natural or legal person; or for reasons of important
                  public interest.
                </p>
                <p className="text section__text _mb-13">
                  You have the right to object to our processing of your
                  personal data on grounds relating to your particular
                  situation, but only to the extent that the legal basis for the
                  processing is that the processing is necessary for: the
                  performance of a task carried out in the public interest or in
                  the exercise of any official authority vested in us; or the
                  purposes of the legitimate interests pursued by us or by a
                  third party. If you make such an objection, we will cease to
                  process the personal information unless we can demonstrate
                  compelling legitimate grounds for the processing which
                  override your interests, rights and freedoms, or the
                  processing is for the establishment, exercise or defence of
                  legal claims.
                </p>
                <p className="text section__text _mb-13">
                  You have the right to object to our processing of your
                  personal data for direct marketing purposes (including
                  profiling for direct marketing purposes). If you make such an
                  objection, we will cease to process your personal data for
                  this purpose.
                </p>
                <p className="text section__text _mb-13">
                  You have the right to object to our processing of your
                  personal data for scientific or historical research purposes
                  or statistical purposes on grounds relating to your particular
                  situation, unless the processing is necessary for the
                  performance of a task carried out for reasons of public
                  interest.
                </p>
                <p className="text section__text _mb-13">
                  To the extent that the legal basis for our processing of your
                  personal data is:
                </p>
                <ul className="list list_disc _mb-13">
                  <li className="text list__item section__text _mb-7">
                    consent; or
                  </li>
                  <li className="text list__item section__text">
                    that the processing is necessary for the performance of a
                    contract to which you are party or in order to take steps at
                    your request prior to entering into a contract,
                  </li>
                </ul>
                <p className="text section__text _mb-13">
                  and such processing is carried out by automated means, you
                  have the right to receive your personal data from us in a
                  structured, commonly used and machine-readable format.
                  However, this right does not apply where it would adversely
                  affect the rights and freedoms of others.
                </p>
                <p className="text section__text _mb-13">
                  If you consider that our processing of your personal
                  information infringes data protection laws, you have a legal
                  right to lodge a complaint with a supervisory authority
                  responsible for data protection. You may do so in the EU
                  member state of your habitual residence, your place of work or
                  the place of the alleged infringement.
                </p>
                <p className="text section__text _mb-13">
                  To the extent that the legal basis for our processing of your
                  personal information is consent, you have the right to
                  withdraw that consent at any time. Withdrawal will not affect
                  the lawfulness of processing before the withdrawal.
                </p>
                <p className="text section__text _mb-23">
                  You may exercise any of your rights in relation to your
                  personal data by written notice to us.
                </p>

                <h5 className="title section__title _mb-17">
                  9. Third party websites
                </h5>
                <p className="text section__text _mb-23">
                  Our website includes hyperlinks to, and details of, third
                  party websites. We have no control over, and are not
                  responsible for, the privacy policies and practices of third
                  parties.
                </p>

                <h5 className="title section__title _mb-17">
                  10. Updating information
                </h5>
                <p className="text section__text _mb-23">
                  Please let us know if the personal information that we hold
                  about you needs to be corrected or updated.
                </p>

                <h5 className="title section__title _mb-17">
                  11. Acting as a data processor
                </h5>
                <p className="text section__text _mb-13">
                  In respect of personal data stored in the database for your
                  Siteplus website, we do not usually act as a data controller;
                  instead, we act as a data processor. Insofar as we act as a
                  data processor rather than a data controller, this policy
                  shall not apply.
                </p>
                <p className="text section__text _mb-23">
                  Our legal obligations as a data processor are instead set out
                  in the contract between us and you, or between us and the
                  relevant data controller.
                </p>

                <h5 className="title section__title _mb-17">
                  12. About cookies
                </h5>
                <p className="text section__text _mb-13">
                  A cookie is a file containing an identifier (a string of
                  letters and numbers) that is sent by a web server to a web
                  browser and is stored by the browser. The identifier is then
                  sent back to the server each time the browser requests a page
                  from the server.
                </p>
                <p className="text section__text _mb-13">
                  Cookies may be either "persistent" cookies or "session"
                  cookies: a persistent cookie will be stored by a web browser
                  and will remain valid until its set expiry date, unless
                  deleted by the user before the expiry date; a session cookie,
                  on the other hand, will expire at the end of the user session,
                  when the web browser is closed.
                </p>
                <p className="text section__text _mb-13">
                  Cookies do not typically contain any information that
                  personally identifies a user, but personal information that we
                  store about you may be linked to the information stored in and
                  obtained from cookies.
                </p>
                <p className="text section__text _mb-23">
                  Local storage is the storage of data sent by our web server to
                  your web browser. This data is not sent back to the web
                  server.‌
                </p>

                <h5 className="title section__title _mb-17">
                  13. Cookies and local storage that we use
                </h5>
                <p className="text section__text _mb-13">
                  We use cookies for the following purposes:
                </p>
                <ul className="list list_disc _mb-13">
                  <li className="text list__item section__text _mb-7">
                    authentication - we use cookies to identify you when you
                    visit our website and as you navigate our website;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    status - we use cookies to help us to determine if you are
                    logged into our website;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    shopping cart - we use cookies to maintain the state of your
                    shopping cart as you navigate our website;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    personalisation - we use cookies to store information about
                    your preferences and to personalise our website for you;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    security - we use cookies as an element of the security
                    measures used to protect user accounts, including preventing
                    fraudulent use of login credentials, and to protect our
                    website and services generally;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    analysis - we use cookies to help us to analyse the use and
                    performance of our website and services; and
                  </li>
                  <li className="text list__item section__text">
                    cookie consent - we use cookies to store your preferences in
                    relation to the use of cookies more generally.
                  </li>
                </ul>
                <p className="text section__text _mb-23">
                  We use local storage for the following purposes: (a) browser
                  tab synchronisation - to synchronise state, available features
                  and limits between different web browser tabs; (b) save
                  settings - to save your sidebar lock / unlock settings; and
                  (c) ecommerce – to facilitate ecommerce functionality.
                </p>

                <h5 className="title section__title _mb-17">
                  14. Cookies use by our service providers
                </h5>
                <p className="text section__text _mb-13">
                  We use Google Analytics to analyse the use of our website.
                  Google Analytics gathers information about website use by
                  means of cookies. The information gathered relating to our
                  website is used to create reports about the use of our
                  website. Google's privacy policy is available at:{' '}
                  <a
                    className="link link_blue section__link"
                    href="https://www.google.com/policies/privacy/"
                    target="_blank"
                  >
                    https://www.google.com/policies/privacy/
                  </a>
                  .
                </p>
                <p className="text section__text _mb-13">
                  From time to time we may use other analytics services
                  providers to help us to monitor the use of our website and
                  services. These services providers will use cookies to collect
                  information about the use of the website and services.
                </p>
                <p className="text section__text _mb-23">
                  We use Cloudflare to identify malicious visitors to our
                  website, to reduce the chance of blocking legitimate users,
                  and to provide customised services. For these purposes,
                  Cloudflare uses a cookie. Find out more about the Cloudflare
                  cookies at{' '}
                  <a
                    className="link link_blue section__link"
                    href="https://www.cloudflare.com/cookie-policy/"
                    target="_blank"
                  >
                    https://www.cloudflare.com/cookie-policy/
                  </a>
                  .
                </p>

                <h5 className="title section__title _mb-17">
                  15. Managing cookies
                </h5>
                <p className="text section__text _mb-13">
                  Our website includes cookie management options. For these
                  purposes, there are three different categories of cookies.
                  First, cookies that are necessary for the operation of our
                  website and the provision of our services. Second, cookies
                  that enhance the performance and functionality of our website
                  and services. Third, cookies that we use for analytics
                  purposes. To use our website and services, your browser must
                  accept the necessary cookies. However, you may accept or
                  reject the performance, functionality and analytics cookies.
                  If you do not accept the performance and functionality
                  cookies, this will negatively affect the performance of our
                  website and some functionality on our website will be
                  unavailable to you. You can{' '}
                  <a
                    className="link link_blue section__link"
                    onClick={utils.openSettingsPopup}
                  >
                    manage the cookies
                  </a>{' '}
                  that you accept from our website.
                </p>
                <p className="text section__text _mb-13">
                  Most browsers allow you to refuse to accept cookies and to
                  delete cookies. The methods for doing so vary from browser to
                  browser, and from version to version. You can however obtain
                  up-to-date information about blocking and deleting cookies via
                  these links:
                </p>
                <ul className="list list_disc _mb-13">
                  <li className="text list__item section__text _mb-7">
                    <a
                      className="link link_blue section__link"
                      href="https://support.google.com/chrome/answer/95647?hl=en"
                      target="_blank"
                    >
                      https://support.google.com/chrome/answer/95647?hl=en
                    </a>{' '}
                    (Chrome);
                  </li>
                  <li className="text list__item section__text _mb-7">
                    <a
                      className="link link_blue section__link"
                      href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences "
                      target="_blank"
                    >
                      https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences
                    </a>{' '}
                    (Firefox);
                  </li>
                  <li className="text list__item section__text _mb-7">
                    <a
                      className="link link_blue section__link"
                      href="http://www.opera.com/help/tutorials/security/cookies/"
                      target="_blank"
                    >
                      http://www.opera.com/help/tutorials/security/cookies/
                    </a>{' '}
                    (Opera);
                  </li>
                  <li className="text list__item section__text _mb-7">
                    <a
                      className="link link_blue section__link"
                      href="https://support.microsoft.com/en-gb/help/17442/windows-internet-explorer-delete-manage-cookies"
                      target="_blank"
                    >
                      https://support.microsoft.com/en-gb/help/17442/windows-internet-explorer-delete-manage-cookies
                    </a>{' '}
                    (Internet Explorer);
                  </li>
                  <li className="text list__item section__text _mb-7">
                    <a
                      className="link link_blue section__link"
                      href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471"
                      target="_blank"
                    >
                      https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471
                    </a>{' '}
                    (Safari); and
                  </li>
                  <li className="text list__item section__text">
                    <a
                      className="link link_blue section__link"
                      href="https://privacy.microsoft.com/en-us/windows-10-microsoft-edge-and-privacy"
                      target="_blank"
                    >
                      https://privacy.microsoft.com/en-us/windows-10-microsoft-edge-and-privacy
                    </a>{' '}
                    (Edge).
                  </li>
                </ul>
                <p className="text section__text _mb-13">
                  Blocking all cookies will have a negative impact upon the
                  usability of many websites.
                </p>
                <p className="text section__text _mb-23">
                  If you block cookies, you will not be able to use all the
                  features on our website.
                </p>

                <h5 className="title section__title _mb-17">16. Our details</h5>
                <p className="text section__text _mb-13">
                  This website is owned and operated by Siteplus Pte Ltd.
                </p>
                <p className="text section__text _mb-13">
                  We are registered in Singapore under registration number
                  201614837Z, and our registered office is at 10, ANSON ROAD,
                  #22-02, INTERNATIONAL PLAZA, SINGAPORE 079903.
                </p>
                <p className="text section__text _mb-13">You can contact us:</p>
                <ul className="list list_disc _mb-13">
                  <li className="text list__item section__text _mb-7">
                    by post, to the postal address given above;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    using our website contact form;
                  </li>
                  <li className="text list__item section__text _mb-7">
                    by telephone, on the contact number published on our website
                    from time to time; or
                  </li>
                  <li className="text list__item section__text">
                    by email, using the email address published on our website
                    from time to time.
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Fragment>
  );
};

export default PrivacyPolicy;
